/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Button, Select } from "@chakra-ui/react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Tooltip, IconButton } from "@mui/material";
import { MdOutlineContentCopy, MdOutlineCheckCircle } from "react-icons/md";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import axios from "axios";
import InputField from "components/fields/InputField";

const rowPerPage = 10;

type ReceiverData = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  middle_name: string;
  gender: string;
  dob: string;
  dialcode: string;
  phone: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  status: string;
  kyc_status: string;
  created_at: string;
  subaccount?: {
    id: string;
    name: string;
  };
};

interface PayoutReceiverListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (receiverData: ReceiverData) => void;
  subaccountId?: string;
}

interface PayloadType {
  [key: string]: any;
}

const initialFilters = {
  field: "",
  keyword: "",
};

const PayoutReceiverListModal: React.FC<PayoutReceiverListModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  subaccountId,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<ReceiverData[]>([]);

  // Filter state
  const [filters, setFilters] = useState(initialFilters);

  // Pagination state following receiver/index.tsx pattern
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(rowPerPage);
  const [rowCount, setRowCount] = useState<number>(rowPerPage);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  // Fetch when modal opens
  useEffect(() => {
    if (isOpen) {
      setTableData([]);
      setPage(0);
      setLastKeys({});
      setCurrentLastKey("");
      setRowCount(rowPerPage);
      setFilters(initialFilters);
      fetchReceivers({
        page: 0,
        limit: pageSize,
        lastkey: "",
        ...initialFilters,
      });
    }
  }, [isOpen]);

  const handleInputChange = (event: any) => {
    const { id, value } = event.target;
    setFilters({ ...filters, [id]: value });
  };

  const handleSearchSubmit = () => {
    setPage(0);
    setLastKeys({});
    setCurrentLastKey("");
    fetchReceivers({
      page: 0,
      limit: pageSize,
      lastkey: "",
      ...filters,
    });
  };

  const handleSearchClear = () => {
    setFilters(initialFilters);
    setPage(0);
    setLastKeys({});
    setCurrentLastKey("");
    fetchReceivers({
      page: 0,
      limit: pageSize,
      lastkey: "",
      ...initialFilters,
    });
  };

  const fetchReceivers = async (payload: PayloadType = {}) => {
    try {
      setIsLoading(true);

      const queryObject: PayloadType = {
        limit: payload?.limit || pageSize,
        lastkey: payload?.lastkey || "",
      };

      // Add filter parameters
      if (payload?.field && payload?.keyword) {
        queryObject[payload.field] = payload.keyword;
      }
      // Filter by subaccount if provided (superadmin flow)
      if (subaccountId) {
        queryObject.subaccount_id = subaccountId;
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/receiver/func/get-receivers?${queryString}`
      );

      const newData: ReceiverData[] = response.data.receivers?.payload || [];

      if (newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      // Manage Last Key and Total Record (following receiver/index.tsx pattern)
      const newLastKey = response.data.receivers?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        const newPage = payload?.page || 0;
        const newPageSize = payload?.limit || pageSize;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys((prev) => ({ ...prev, [newPage]: newLastKey }));
      } else {
        // No more pages
        const newPage = payload?.page || 0;
        const newPageSize = payload?.limit || pageSize;
        setRowCount(newPage * newPageSize + newData.length);
        setCurrentLastKey("");
      }
    } catch (error) {
      console.error("Error fetching receivers", error);
      setTableData([]);
      // Clear all
      setPage(0);
      setRowCount(0);
      setLastKeys({});
      setCurrentLastKey("");
    } finally {
      setIsLoading(false);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newPayload: PayloadType = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      ...filters,
    };

    // If click prev pagination
    if (newValue.page < page) {
      newPayload = {
        ...newPayload,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newPayload = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        ...filters,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys({});
    } else {
      setPage(newValue.page);
    }

    fetchReceivers(newPayload);
  };

  const handleSelect = (receiver: ReceiverData) => {
    onSelect(receiver);
    onClose();
  };

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "",
      flex: 1,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<ReceiverData, string>) => (
        <Tooltip title="Copy Receiver ID" arrow placement="top">
          <div>
            <IconButton
              id={"button-copy-receiverid-" + params?.row?.id}
              onClick={() => handleClickCopy(params.row.id)}
            >
              <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
            </IconButton>
          </div>
        </Tooltip>
      ),
    },
    {
      field: "subaccount",
      headerName: "Subaccount",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ReceiverData, any>) =>
        params.row.subaccount?.name || "-",
    },
    {
      field: "first_name",
      headerName: "First Name",
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      flex: 1,
      minWidth: 100,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        return (
          <Tooltip title="Select Receiver" arrow placement="top">
            <IconButton
              id={"button-select-" + params?.row?.id}
              onClick={() => handleSelect(params.row)}
            >
              <MdOutlineCheckCircle className="h-5 w-5" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const filterColumns = () => {
    let rebuildColumns = [];
    if (userRole !== "superadmin") {
      const excludeFields = ["subaccount"];
      rebuildColumns = columns.filter(
        (column) => !excludeFields.includes(column?.field)
      );
    } else {
      rebuildColumns = columns;
    }
    return rebuildColumns;
  };

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start",
    row: "",
  };

  return (
    <>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          closeOnOverlayClick={false}
          size="6xl"
        >
          <ModalOverlay className="bg-[#000] !opacity-30" />
          <ModalContent className="dark:!bg-neutral-900 !z-[9999] !m-auto !w-full !max-w-[90%] !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[3vh] lg:!max-w-[1100px]">
            <ModalBody className="p-0">
              <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/10 md:px-7">
                <div className="flex items-start gap-3">
                  <div className="mt-1 rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-300 dark:ring-primary-900/30">
                    <svg
                      viewBox="0 0 24 24"
                      className="h-6 w-6"
                      fill="currentColor"
                    >
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Select Receiver
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Select a receiver for your payout
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-3xl border border-gray-200 text-gray-600 transition hover:bg-gray-50 hover:text-gray-700 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
                  aria-label="Close"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 6 6 18" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body (scroll) */}
              <div className="max-h-[70vh] overflow-y-auto">
                {/* Sticky Filter */}
                <div className="dark:bg-neutral-900/85 sticky top-0 z-10 border-b border-gray-100 bg-white/90 px-6 py-4 backdrop-blur dark:border-white/10 md:px-7">
                  <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="tracking-tight text-gray-900">
                      <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                        Field
                      </label>
                      <Select
                        isDisabled={isLoading}
                        id="field"
                        value={filters.field}
                        onChange={handleInputChange}
                        className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
                      >
                        <option value="">Select Field</option>
                        <option value="first_name">First Name</option>
                        <option value="last_name">Last Name</option>
                        <option value="email">Email</option>
                        <option value="receiver_id">Receiver ID</option>
                      </Select>
                    </div>
                    <div className="tracking-tight text-gray-900">
                      <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                        Keyword
                      </label>
                      <InputField
                        disabled={isLoading}
                        variant="auth"
                        extra="shadow-sm rounded-md"
                        label=""
                        placeholder=""
                        id="keyword"
                        type="text"
                        autoComplete="off"
                        value={filters.keyword}
                        onChange={handleInputChange}
                        hideLabel
                      />
                    </div>
                    <div className="flex items-end justify-end gap-2">
                      <Button
                        isDisabled={isLoading}
                        onClick={handleSearchClear}
                        className="dark:bg-neutral-900 h-10 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-60 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                      >
                        Clear
                      </Button>

                      <Button
                        isDisabled={isLoading}
                        onClick={handleSearchSubmit}
                        isLoading={isLoading}
                        loadingText="Searching..."
                        className="btn-primary h-10 rounded-lg px-5 text-sm font-semibold shadow-sm disabled:opacity-60"
                      >
                        Search
                      </Button>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Tip: Select filters and click Search.
                  </div>
                </div>

                {/* Table */}
                <div className="px-6 py-5 md:px-7">
                  <Box className="inner-border w-full overflow-hidden rounded-b-xl rounded-t-lg bg-white">
                    <DataGrid
                      sx={{
                        "& .MuiDataGrid-cell": { padding: "8px 16px" },
                        "& .MuiDataGrid-columnHeader": { padding: "8px 16px" },
                        "& .MuiTablePagination-input": { marginRight: "-20px" },
                        "& .MuiTablePagination-displayedRows": {
                          display: "none",
                        },
                        border: "none",
                      }}
                      loading={isLoading}
                      rows={tableData || []}
                      columns={filterColumns()}
                      getRowId={(row) => row.id}
                      rowCount={rowCount}
                      sortingMode="server"
                      paginationMode="server"
                      pageSizeOptions={[10]}
                      rowHeight={48}
                      columnHeaderHeight={48}
                      paginationModel={{ page: page, pageSize: pageSize }}
                      onPaginationModelChange={setPaginationModel}
                      disableRowSelectionOnClick
                      disableColumnFilter
                      disableColumnMenu
                      classes={customDataGridStyles}
                      hideFooter={currentLastKey === "" && page === 0}
                      autoHeight
                      localeText={{
                        noRowsLabel: "No receivers found",
                      }}
                    />
                  </Box>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4 dark:border-white/10 md:px-7">
                <Button
                  type="button"
                  onClick={onClose}
                  className="dark:bg-neutral-900 h-10 rounded-lg border border-gray-200 bg-white px-5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                >
                  Close
                </Button>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default PayoutReceiverListModal;
